import { CardBackground, Typography } from "@hellocarbo/lasagne";
import { descendingSort } from "@hellocarbo/lasagne/utils";
import BarChart, { ECOption } from "@ui/BarChart";
import ChartTooltip from "@ui/ChartTooltip";
import { TooltipComponentOption } from "echarts/components";
import { TFunction } from "i18next";
import { useEffect, useRef, useState } from "react";
import { renderToString } from "react-dom/server";
import { Trans, useTranslation } from "react-i18next";

import { CategoryStyles } from "@/config";
import { ReviewCategory, Subsidiary } from "@/types";
import { formatCarbon, getCarbonDisplayUnit } from "@/utils/carbon";
import { formatEndDate } from "@/utils/formatDate";

import colors from "../../../../colors";

// Only the entities with the highest emissions are displayed
const MAX_ENTITIES = 5;
// Approximate width taken by the y axis labels (e.g. "1 200 tCO2e")
const Y_AXIS_LABELS_WIDTH = 70;
// Below this width, the values displayed above the bars overlap each other
const MIN_BAR_WIDTH_FOR_VALUES = 28;

// Tracks the width of an element so the chart layout can adapt to it
function useElementWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (!ref.current) return;

    const observer = new ResizeObserver(([entry]) =>
      setWidth(entry.contentRect.width)
    );
    observer.observe(ref.current);

    return () => observer.disconnect();
  }, []);

  return { ref, width };
}

function optionFactory(
  entities: Array<Subsidiary>,
  categoryKeys: Array<string>,
  chartWidth: number,
  t: TFunction
): ECOption {
  // Each entity gets an equal share of the plot area: its name wraps within it
  const entityWidth =
    (chartWidth - Y_AXIS_LABELS_WIDTH) / Math.max(entities.length, 1);
  const labelWidth = Math.max(entityWidth - 8, 40);
  // barCategoryGap is 30%, the remaining 70% is shared by the bars of an entity
  const barWidth = (entityWidth * 0.7) / Math.max(categoryKeys.length, 1);

  const carbonOf = (entity: Subsidiary, key: string) =>
    entity.review_categories?.find((category) => category.key === key)
      ?.carbon ?? 0;

  const unit = getCarbonDisplayUnit(
    entities.flatMap((entity) =>
      categoryKeys.map((key) => carbonOf(entity, key))
    )
  );

  return {
    textStyle: {
      fontFamily: "Poppins",
      color: colors["typography"],
    },
    legend: {
      bottom: 0,
      icon: "circle",
      itemWidth: 10,
      itemHeight: 10,
      textStyle: { fontSize: 12, color: colors["typography"] },
    },
    tooltip: {
      borderWidth: 0,
      padding: 0,
      extraCssText: "border-radius: 8px;",
      formatter: ({
        seriesName,
        value,
        color,
      }: {
        seriesName: string;
        value: number;
        color: string;
      }) =>
        renderToString(
          <ChartTooltip
            seriesName={seriesName}
            valueToDisplay={t("formatter:number", { value })}
            unit={t(`common:unit.${unit}`)}
            color={color}
            iconClassName="size-2xs"
          />
        ),
    } as TooltipComponentOption,
    grid: {
      left: "0%",
      right: "0%",
      top: "8%",
      bottom: "12%",
      containLabel: true,
    }, // Configure the grid
    xAxis: {
      type: "category",
      data: entities.map((entity) => entity.company_name),
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        // Show every entity name, wrapped on several lines if too long
        interval: 0,
        width: labelWidth,
        overflow: "break",
        lineHeight: 16,
        fontWeight: 600,
        fontSize: 12,
      },
    },
    yAxis: {
      type: "value",
      axisLabel: {
        formatter: (value: number) =>
          `${t("formatter:number", { value })} ${t(`common:unit.${unit}`)}`,
        fontSize: 10,
        color: colors["grayscale"].DEFAULT,
      },
    },
    series: categoryKeys.map((key) => ({
      type: "bar",
      name: t(`reviewCategories:${key}`),
      itemStyle: {
        color: CategoryStyles[key]?.color,
        borderRadius: [4, 4, 0, 0],
      },
      barGap: "10%",
      barCategoryGap: "30%",
      label: {
        show: barWidth >= MIN_BAR_WIDTH_FOR_VALUES,
        position: "top",
        fontSize: 10,
        color: "inherit",
        formatter: ({ value }) => t("formatter:number", { value }),
      },
      data: entities.map(
        (entity) => formatCarbon(t, carbonOf(entity, key), unit).convertedCarbon
      ),
    })),
  };
}

function ReviewByEntities({
  subsidiaries,
  categories,
  periodStart,
  periodEnd,
}: {
  subsidiaries: Array<Subsidiary>;
  categories: Array<ReviewCategory>;
  periodStart: string;
  periodEnd: string;
}) {
  const { t } = useTranslation(["common", "dashboard", "formatter"]);

  const entities = [...subsidiaries]
    .sort((a, b) => descendingSort(a.carbon, b.carbon))
    .slice(0, MAX_ENTITIES);
  const categoryKeys = categories.map((category) => category.key);
  const { ref: chartRef, width: chartWidth } =
    useElementWidth<HTMLDivElement>();

  return (
    <CardBackground>
      <div className="mb-md gap-x-sm flex flex-wrap items-baseline">
        <Typography step="2" as="h2" weight="bold">
          {t("dashboard:consolidation.entities_breakdown")}
        </Typography>
        <Typography step="0">
          <Trans
            t={t}
            i18nKey="dashboard:consolidation.dates"
            values={{
              from: t("formatter:date", { value: new Date(periodStart) }),
              to: t("formatter:date", {
                value: formatEndDate(new Date(periodEnd)),
              }),
            }}
            components={{ 1: <span className="font-bold" /> }}
          />
        </Typography>
      </div>
      <div ref={chartRef} className="mb-md h-[400px]">
        <BarChart
          option={optionFactory(entities, categoryKeys, chartWidth, t)}
        />
      </div>
      <Typography step="-1" className="text-grayscale">
        {t("dashboard:consolidation.entities_breakdown_limit")}
      </Typography>
    </CardBackground>
  );
}

export default ReviewByEntities;
